<script setup lang="ts">
interface GenicPrompt {
    text: string;
    dot: string;
}

interface GenicOrderCard {
    title: string;
    steps: string[];
    etaIcon: string;
    etaText: string;
    mapFrom: string;
    mapTo: string;
    statusIcon: string;
    statusTitle: string;
    statusSub: string;
}

const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== "dark");

// Stepper node colors (moved from scoped CSS to inline Tailwind)
const stepClasses = [
    "bg-[#a855f7] shadow-[0_0_12px_rgba(168,85,247,0.8)]",
    "bg-[#6d5df6] shadow-[0_0_10px_rgba(109,93,246,0.7)]",
    "bg-transparent border-2 border-[#34d399] after:hidden",
];

defineProps({
    headingLine1: {
        type: String,
        default: "Experience the Future of",
    },
    headingLine2: {
        type: String,
        default: "AI Interfaces with",
    },
    headingAccent: {
        type: String,
        default: "GenicUI",
    },
    prompts: {
        type: Array as () => GenicPrompt[],
        default: () => [
            { text: "Show my recent order status", dot: "#34D399" },
            { text: "Show my recent complaint status", dot: "#60A5FA" },
            { text: "Show my revenue of 7 days", dot: "#A78BFA" },
        ],
    },
    promptLabel: {
        type: String,
        default: "Prompt",
    },
    orderCard: {
        type: Object as () => GenicOrderCard,
        default: () => ({
            title: "Order #23147",
            steps: ["Processing", "Shipped", "Delivered"],
            etaIcon: "streamline:shipping-truck",
            etaText: "Arriving Tomorrow",
            mapFrom: "San Francisco",
            mapTo: "New York",
            statusIcon: "streamline:shipping-box-1",
            statusTitle: "Shipped",
            statusSub: "On track to arrive tomorrow",
        }),
    },
    supportCard: {
        type: Object,
        default: () => ({
            id: "#4821",
            status: "Open",
            title: "Payment Issue",
            created: "Created 2h ago",
            ownerIcon: "streamline:user-single-neutral-male",
            owner: "Customer Support",
            historyTitle: "Resolution History",
            history: [
                {
                    id: "#4820",
                    status: "Closed",
                    note: "Refund not initiated.",
                    created: "Created 14 days ago",
                },
                {
                    id: "#4819",
                    status: "Closed",
                    note: "Order not received.",
                    created: "Created 2 months ago",
                },
            ],
        }),
    },
    revenueCard: {
        type: Object,
        default: () => ({
            title: "Revenue",
            range: "Last 7 days",
            bars: [55, 70, 50, 62, 85, 72, 100, 58, 45],
            accentIndex: 7,
            total: "$12,430",
            delta: "12%",
            metricsTitle: "Key Metrics",
            metrics: [
                {
                    icon: "streamline:shopping-cart-1",
                    label: "Orders",
                    value: "152",
                    delta: "↑ 8%",
                    up: true,
                },
                {
                    icon: "streamline:user-multiple-group",
                    label: "New Customers",
                    value: "38",
                    delta: "↑ 15%",
                    up: true,
                },
                {
                    icon: "streamline:interface-edit-view-eye-eyeball-open-view",
                    label: "Page Views",
                    value: "4,892",
                    delta: "↑ 6%",
                    up: true,
                },
            ],
        }),
    },
});

const sectionRef = ref<HTMLElement | null>(null);
const glowRef = ref<HTMLElement | null>(null);

let glowRafId = 0;

function onSectionMove(e: MouseEvent) {
    if (glowRafId) return;
    glowRafId = requestAnimationFrame(() => {
        glowRafId = 0;
        const section = sectionRef.value;
        const glow = glowRef.value;
        if (!section || !glow) return;
        const rect = section.getBoundingClientRect();
        glow.style.transform = `translate(${e.clientX - rect.left - 280}px, ${e.clientY - rect.top - 280}px)`;
    });
}

onUnmounted(() => cancelAnimationFrame(glowRafId));
</script>

<template>
    <div ref="sectionRef" class="group relative overflow-visible" @mousemove="onSectionMove">
        <!-- Cursor-following glow (only visible while hovering the section) -->
        <div ref="glowRef" aria-hidden="true"
            class="absolute left-0 top-0 z-0 size-[560px] rounded-full pointer-events-none opacity-0 group-hover:opacity-100 [transition:transform_0.15s_ease-out,opacity_0.4s_ease] mix-blend-normal bg-[radial-gradient(circle_closest-side,rgba(139,131,255,0.18)_0%,rgba(124,108,246,0.08)_38%,rgba(124,108,246,0.03)_65%,transparent_100%)] dark:mix-blend-screen dark:bg-[radial-gradient(circle_closest-side,rgba(139,131,255,0.14)_0%,rgba(124,108,246,0.06)_38%,rgba(124,108,246,0.025)_65%,transparent_100%)]"></div>
        <div class="relative z-1 mx-auto max-w-[1280px] px-4">
            <h2 class="text-center font-avant-demi">
                <WrGradientText :text="headingLine1"
                    append-class="p-0 bg-[linear-gradient(92.15deg,_var(--text-gradient-1)_-3.57%,_var(--text-gradient-2)_80.72%)]" />
                <span class="text-[#1E1B4B] dark:text-white !font-normal">{{ headingLine2 + " " }}</span>
                <WrGradientText :text="headingAccent"
                    append-class="!font-normal inline p-0 bg-[linear-gradient(92.15deg,#8B7BFF_-3.57%,#BA55FE_80.72%)]" />
            </h2>

            <!-- Prompt → GenicUI → live components diagram -->
            <div
                class="font-avant relative mt-8 lg:mt-12 grid grid-cols-1 lg:grid-cols-[minmax(0,300px)_minmax(40px,1fr)_auto_minmax(40px,1fr)_minmax(0,360px)] xl:grid-cols-[361px_minmax(60px,1fr)_auto_minmax(60px,1fr)_469px] items-center gap-y-10 xl:scale-[0.8] xl:origin-top">
                <!-- Dot-grid backdrop -->
                <div aria-hidden="true"
                    class="absolute z-[-1] pointer-events-none top-[-120px] bottom-[-120px] left-[-48px] right-[-48px] bg-[size:22px_22px] bg-[radial-gradient(circle,rgba(109,90,220,0.4)_2px,transparent_2.5px)] dark:bg-[radial-gradient(circle,rgba(129,116,255,0.38)_2px,transparent_2.5px)] [mask-image:radial-gradient(ellipse_52%_58%_at_50%_50%,#000_20%,transparent_70%)] [-webkit-mask-image:radial-gradient(ellipse_52%_58%_at_50%_50%,#000_20%,transparent_70%)]"></div>

                <!-- Left: prompt cards -->
                <div
                    class="flex flex-col gap-7 lg:gap-9 mx-auto w-full max-w-[361px] md:max-w-none md:grid md:grid-cols-3 md:gap-4 lg:flex lg:flex-col xl:mx-0 xl:max-w-none">
                    <article v-for="(prompt, index) in prompts" :key="prompt.text"
                        class="prompt-card relative rounded-2xl border p-4 px-5 xl:h-[105px] border-[rgba(139,131,255,0.4)] dark:border-[rgba(139,131,255,0.35)] bg-[linear-gradient(196.53deg,rgba(255,255,255,0.9)_61.83%,#f3f0ff_88.29%)] dark:bg-[linear-gradient(196.53deg,rgba(4,7,23,0.65)_61.83%,#03031f_88.29%)] shadow-[0_18px_40px_-18px_rgba(93,70,190,0.35)] dark:shadow-[0_18px_40px_-18px_rgba(0,0,0,0.7)]"
                        :style="{ '--genic-delay': (index % 3) * 3 + 's' }">
                        <p class="flex items-center gap-2 text-base font-semibold text-[#1E1B4B] dark:text-white">
                            <span class="inline-block size-[10px] rounded-full flex-shrink-0"
                                :style="{ background: prompt.dot }"></span>
                            {{ promptLabel }}
                        </p>
                        <p class="mt-1.5 text-[#5B5878] dark:text-[#C9CDEB] leading-normal">“{{ prompt.text }}”
                        </p>
                    </article>
                </div>

                <!-- Left connectors -->
                <svg class="hidden lg:block h-full w-full self-stretch" viewBox="0 0 72 100" preserveAspectRatio="none"
                    aria-hidden="true">
                    <defs>
                        <linearGradient id="genic-conn-grad-l" x1="0" y1="0" x2="72" y2="0"
                            gradientUnits="userSpaceOnUse">
                            <stop offset="0" :stop-color="isLight ? '#d6ccff' : '#08163A'" />
                            <stop offset="1" stop-color="#3B38F8" />
                        </linearGradient>
                        <linearGradient id="genic-shimmer-l" x1="-20" y1="0" x2="20" y2="0"
                            gradientUnits="userSpaceOnUse">
                            <stop offset="0" stop-color="#fff" stop-opacity="0" />
                            <stop offset="0.5" stop-color="#fff" stop-opacity="0.9" />
                            <stop offset="1" stop-color="#fff" stop-opacity="0" />
                            <animateTransform attributeName="gradientTransform" type="translate" from="-30 0" to="102 0"
                                dur="2.4s" repeatCount="indefinite" />
                        </linearGradient>
                    </defs>
                    <g fill="none" stroke="url(#genic-conn-grad-l)" stroke-width="2">
                        <path d="M0 32 C 36 32, 36 50, 72 50" vector-effect="non-scaling-stroke" />
                        <path d="M0 50 C 36 50, 36 50, 72 50" vector-effect="non-scaling-stroke" />
                        <path d="M0 68 C 36 68, 36 50, 72 50" vector-effect="non-scaling-stroke" />
                    </g>
                    <g class="drop-shadow-[0_0_4px_rgba(139,123,255,0.5)] dark:drop-shadow-[0_0_4px_rgba(59,56,248,0.8)] motion-reduce:hidden"
                        fill="none" stroke="url(#genic-shimmer-l)" stroke-width="2" stroke-linecap="round">
                        <path d="M0 32 C 36 32, 36 50, 72 50" vector-effect="non-scaling-stroke" />
                        <path d="M0 50 C 36 50, 36 50, 72 50" vector-effect="non-scaling-stroke" />
                        <path d="M0 68 C 36 68, 36 50, 72 50" vector-effect="non-scaling-stroke" />
                    </g>
                </svg>

                <!-- Center: GenicUI card (vertical flow lines below lg, where the
                     horizontal SVG connectors are hidden) -->
                <div
                    class="mx-auto relative w-[150px] h-[158px] rounded-3xl border-2 border-[#8b7bff] flex flex-col items-center justify-center gap-2.5 bg-[radial-gradient(120%_120%_at_50%_0%,#ffffff_0%,#f3eeff_55%)] dark:bg-[radial-gradient(120%_120%_at_50%_0%,#141a45_0%,#0a0e28_55%)] shadow-[0_0_30px_rgba(139,123,255,0.25),inset_0_0_24px_rgba(139,123,255,0.08)] dark:shadow-[0_0_30px_rgba(139,123,255,0.35),inset_0_0_24px_rgba(139,123,255,0.12)]
                    before:content-[''] before:absolute before:-top-8 before:left-1/2 before:-translate-x-1/2 before:h-8 before:w-[2px] before:rounded-full before:bg-gradient-to-b before:from-transparent before:to-[#3B38F8] before:lg:hidden
                    after:content-[''] after:absolute after:-bottom-8 after:left-1/2 after:-translate-x-1/2 after:h-8 after:w-[2px] after:rounded-full after:bg-gradient-to-b after:from-[#3B38F8] after:to-transparent after:lg:hidden">
                    <svg class="w-[62px] h-[62px] text-[#7c3aed] dark:text-white drop-shadow-[0_0_10px_rgba(124,58,237,0.35)] dark:drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]"
                        viewBox="0 0 100 100" aria-hidden="true">
                        <path d="M50 6 L88 28 V72 L50 94 L12 72 V28 Z" fill="none" stroke="currentColor"
                            stroke-width="5" stroke-linejoin="round" />
                        <rect x="32" y="30" width="10" height="10" transform="rotate(45 37 35)" fill="currentColor" />
                        <rect x="58" y="30" width="10" height="10" transform="rotate(45 63 35)" fill="currentColor" />
                        <path d="M37 43 L50 57 L63 43" fill="none" stroke="currentColor" stroke-width="6"
                            stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M50 57 L50 86" fill="none" stroke="currentColor" stroke-width="6"
                            stroke-linecap="round" />
                    </svg>
                    <p class="text-lg font-avant-demi text-[#1E1B4B] dark:text-white">GenicUI</p>
                </div>

                <!-- Right connectors -->
                <svg class="hidden lg:block h-full w-full self-stretch overflow-visible" viewBox="0 0 72 100"
                    preserveAspectRatio="none" aria-hidden="true">
                    <defs>
                        <linearGradient id="genic-conn-grad-r" x1="0" y1="0" x2="72" y2="0"
                            gradientUnits="userSpaceOnUse">
                            <stop offset="0" :stop-color="isLight ? '#d6ccff' : '#08163A'" />
                            <stop offset="1" stop-color="#3B38F8" />
                        </linearGradient>
                        <linearGradient id="genic-shimmer-r" x1="-20" y1="0" x2="20" y2="0"
                            gradientUnits="userSpaceOnUse">
                            <stop offset="0" stop-color="#fff" stop-opacity="0" />
                            <stop offset="0.5" stop-color="#fff" stop-opacity="0.9" />
                            <stop offset="1" stop-color="#fff" stop-opacity="0" />
                            <animateTransform attributeName="gradientTransform" type="translate" from="-30 0" to="102 0"
                                dur="2.4s" repeatCount="indefinite" />
                        </linearGradient>
                    </defs>
                    <g fill="none" stroke="url(#genic-conn-grad-r)" stroke-width="2">
                        <path d="M0 50 C 36 50, 36 18, 104 18" vector-effect="non-scaling-stroke" />
                        <path d="M0 50 C 36 50, 36 50, 72 50" vector-effect="non-scaling-stroke" />
                        <path d="M0 50 C 36 50, 36 82, 104 82" vector-effect="non-scaling-stroke" />
                    </g>
                    <g class="drop-shadow-[0_0_4px_rgba(139,123,255,0.5)] dark:drop-shadow-[0_0_4px_rgba(59,56,248,0.8)] motion-reduce:hidden"
                        fill="none" stroke="url(#genic-shimmer-r)" stroke-width="2"
                        stroke-linecap="round">
                        <path d="M0 50 C 36 50, 36 18, 104 18" vector-effect="non-scaling-stroke" />
                        <path d="M0 50 C 36 50, 36 50, 72 50" vector-effect="non-scaling-stroke" />
                        <path d="M0 50 C 36 50, 36 82, 104 82" vector-effect="non-scaling-stroke" />
                    </g>
                </svg>

                <!-- Right: live component cards -->
                <div
                    class="flex flex-col gap-7 lg:gap-9 mx-auto w-full max-w-[469px] md:max-w-none md:grid md:grid-cols-3 md:gap-4 lg:flex lg:flex-col xl:mx-0 xl:max-w-none">
                    <!-- Order status -->
                    <article
                        class="result-card relative rounded-2xl border-[1.25px] border-transparent p-3.5 md:max-lg:p-3 xl:h-[191px] shadow-[0_0_40px_rgba(139,123,255,0.15),0_24px_48px_-24px_rgba(93,70,190,0.3)] dark:shadow-[0_0_40px_rgba(59,56,248,0.12),0_24px_48px_-24px_rgba(0,0,0,0.8)] grid gap-4 xl:grid-cols-[1fr_1.14fr] xl:translate-x-12"
                        style="--genic-delay: 0s">
                        <div
                            class="rounded-[14px] border p-3 md:max-lg:p-2.5 border-[rgba(124,108,246,0.25)] dark:border-[rgba(124,108,246,0.28)] bg-[#f7f5ff] dark:bg-[#131a3e]">
                            <h3 class="text-base font-medium font-avant-demi text-[#1E1B4B] dark:text-white">{{
                                orderCard.title }}</h3>
                            <div class="mt-3 flex items-start justify-between px-1">
                                <template v-for="(step, i) in orderCard.steps" :key="i">
                                    <div class="flex flex-col items-center gap-1.5">
                                        <span
                                            class="relative size-[18px] rounded-full grid place-items-center flex-shrink-0 after:content-[''] after:size-2 after:rounded-full after:bg-white"
                                            :class="stepClasses[i]"></span>
                                        <span class="text-xs text-[#5B5878] dark:text-[#C9CDEB]">{{ step }}</span>
                                    </div>
                                    <span v-if="i < orderCard.steps.length - 1"
                                        :class="['flex-1 h-[2px] mt-2 mx-1', i === 0 ? 'bg-[linear-gradient(90deg,#a855f7,#6d5df6)]' : 'bg-[#cfc9ec] dark:bg-[#3a3f6e]']"></span>
                                </template>
                            </div>
                            <div
                                class="mt-3 flex items-center gap-3 rounded-xl border py-1.5 px-3 border-[rgba(124,108,246,0.3)] dark:border-[rgba(124,108,246,0.35)] bg-[rgba(237,233,254,0.8)] dark:bg-[rgba(24,31,74,0.5)]">
                                <WrIcon :name="orderCard.etaIcon" class="size-5 text-[#67E8F9] flex-shrink-0" />
                                <p class="text-sm font-semibold text-[#1E1B4B] dark:text-white">{{ orderCard.etaText }}
                                </p>
                            </div>
                        </div>
                        <div class="flex flex-col justify-between gap-3.5">
                            <!-- Route map -->
                            <div class="relative h-[90px]" aria-hidden="true">
                                <svg class="absolute inset-0 w-full h-full opacity-85" viewBox="0 0 226 90"
                                    preserveAspectRatio="none" aria-hidden="true">
                                    <path d="M26 70 C 70 80, 150 28, 202 44" fill="none" stroke="#8b7bff"
                                        stroke-width="2" stroke-dasharray="6 5" stroke-linecap="round"
                                        vector-effect="non-scaling-stroke" />
                                </svg>
                                <span class="absolute px-2 py-[3px] rounded-md border text-[11px] whitespace-nowrap border-[rgba(124,108,246,0.3)] dark:border-[rgba(124,108,246,0.35)] bg-[rgba(255,255,255,0.9)] dark:bg-[rgba(13,18,46,0.9)] text-[#5b5878] dark:text-[#c9cdeb]"
                                    style="left: 0; top: 36px;">{{ orderCard.mapFrom }}</span>
                                <span class="absolute grid place-items-center size-3 rounded-full bg-[#8b7bff] shadow-[0_0_10px_rgba(139,123,255,0.9)]"
                                    style="left: 20px; top: 64px;"></span>
                                <span class="absolute px-2 py-[3px] rounded-md border text-[11px] whitespace-nowrap border-[rgba(124,108,246,0.3)] dark:border-[rgba(124,108,246,0.35)] bg-[rgba(255,255,255,0.9)] dark:bg-[rgba(13,18,46,0.9)] text-[#5b5878] dark:text-[#c9cdeb]"
                                    style="right: 0; top: 10px;">{{ orderCard.mapTo }}</span>
                                <span class="absolute grid place-items-center drop-shadow-[0_0_6px_rgba(168,85,247,0.8)]"
                                    style="right: 16px; top: 36px;">
                                    <WrIcon name="streamline:location-pin-3-solid" class="size-4 text-[#a855f7]" />
                                </span>
                            </div>
                            <div
                                class="flex items-center gap-3 rounded-xl border py-1.5 px-3 border-[rgba(124,108,246,0.3)] dark:border-[rgba(124,108,246,0.35)] bg-[rgba(237,233,254,0.8)] dark:bg-[rgba(24,31,74,0.5)]">
                                <WrIcon :name="orderCard.statusIcon" class="size-5 text-[#8B7BFF] flex-shrink-0" />
                                <div class="flex-1 min-w-0">
                                    <p class="text-sm font-medium text-[#1E1B4B] dark:text-white leading-tight">{{
                                        orderCard.statusTitle }}</p>
                                    <p class="text-[11px] text-[#6E6A8F] dark:text-[#8B90B5] leading-tight">{{
                                        orderCard.statusSub }}</p>
                                </div>
                                <WrIcon name="streamline:move-right" class="size-4 text-[#8B90B5] flex-shrink-0" />
                            </div>
                        </div>
                    </article>

                    <!-- Payment issue -->
                    <article
                        class="result-card relative rounded-2xl border-[1.25px] border-transparent p-3.5 md:max-lg:p-3 xl:h-[191px] shadow-[0_0_40px_rgba(139,123,255,0.15),0_24px_48px_-24px_rgba(93,70,190,0.3)] dark:shadow-[0_0_40px_rgba(59,56,248,0.12),0_24px_48px_-24px_rgba(0,0,0,0.8)] grid gap-4 xl:grid-cols-[1fr_1.14fr]"
                        style="--genic-delay: 3s">
                        <div
                            class="rounded-[14px] border p-3 md:max-lg:p-2.5 border-[rgba(124,108,246,0.25)] dark:border-[rgba(124,108,246,0.28)] bg-[#f7f5ff] dark:bg-[#131a3e]">
                            <div class="flex items-center justify-between">
                                <p class="text-base font-medium text-[#1E1B4B] dark:text-white">{{ supportCard.id }}</p>
                                <span class="px-3 py-[3px] rounded-full bg-[#7c5cfc] text-white text-xs font-medium leading-none">{{
                                    supportCard.status }}</span>
                            </div>
                            <h3 class="mt-1.5 text-xl font-avant-demi text-[#1E1B4B] dark:text-white">{{
                                supportCard.title }}</h3>
                            <p class="text-sm text-[#6E6A8F] dark:text-[#8B90B5]">{{ supportCard.created }}</p>
                            <div
                                class="mt-1.5 flex items-center gap-3 rounded-xl border py-1.5 px-3 border-[rgba(124,108,246,0.3)] dark:border-[rgba(124,108,246,0.35)] bg-[rgba(237,233,254,0.8)] dark:bg-[rgba(24,31,74,0.5)]">
                                <span
                                    class="flex-shrink-0 size-8 rounded-full bg-[linear-gradient(135deg,#4b4fd6,#262b7e)] grid place-items-center overflow-hidden">
                                    <WrIcon :name="supportCard.ownerIcon" class="size-5 text-white/90" />
                                </span>
                                <p class="flex-1 text-sm text-[#1E1B4B] dark:text-white">{{ supportCard.owner }}</p>
                                <WrIcon name="streamline:move-right" class="size-4 text-[#8B90B5] flex-shrink-0" />
                            </div>
                        </div>
                        <div>
                            <h4 class="text-base font-medium font-avant-demi text-[#1E1B4B] dark:text-white">{{
                                supportCard.historyTitle }}
                            </h4>
                            <div v-for="(item, i) in supportCard.history" :key="i"
                                class="mt-2.5 rounded-xl border py-1.5 px-3 border-[rgba(124,108,246,0.3)] bg-[rgba(237,233,254,0.8)] dark:bg-[rgba(24,31,74,0.5)]">
                                <div class="flex items-center justify-between">
                                    <p class="text-[11px] leading-none text-[#6E6A8F] dark:text-[#8B90B5]">{{ item.id }}
                                    </p>
                                    <span class="px-2 py-px rounded-full border text-[11px] leading-none border-[rgba(124,108,246,0.35)] bg-[rgba(255,255,255,0.9)] dark:bg-[rgba(19,25,58,0.9)] text-[#6e6a8f] dark:text-[#8b90b5]">{{
                                        item.status }}</span>
                                </div>
                                <p class="mt-0.5 text-sm text-[#1E1B4B] dark:text-white">{{ item.note }}</p>
                                <p class="text-[11px] leading-none text-[#6E6A8F] dark:text-[#8B90B5]">{{ item.created
                                    }}</p>
                            </div>
                        </div>
                    </article>

                    <!-- Revenue -->
                    <article
                        class="result-card relative rounded-2xl border-[1.25px] border-transparent p-3.5 md:max-lg:p-3 xl:h-[191px] shadow-[0_0_40px_rgba(139,123,255,0.15),0_24px_48px_-24px_rgba(93,70,190,0.3)] dark:shadow-[0_0_40px_rgba(59,56,248,0.12),0_24px_48px_-24px_rgba(0,0,0,0.8)] grid gap-4 xl:grid-cols-[1fr_1.14fr] xl:translate-x-12"
                        style="--genic-delay: 6s">
                        <div
                            class="rounded-[14px] border p-3 md:max-lg:p-2.5 border-[rgba(124,108,246,0.25)] dark:border-[rgba(124,108,246,0.28)] bg-[#f7f5ff] dark:bg-[#131a3e] xl:flex xl:flex-col">
                            <div class="flex items-center justify-between gap-3">
                                <h3 class="text-lg font-avant-demi text-[#1E1B4B] dark:text-white">{{ revenueCard.title
                                    }}</h3>
                                <span
                                    class="inline-flex items-center gap-1.5 whitespace-nowrap px-2.5 py-1 rounded-lg border text-[13px] border-[rgba(124,108,246,0.35)] bg-[rgba(255,255,255,0.9)] dark:bg-[rgba(13,18,46,0.8)] text-[#1e1b4b] dark:text-white">
                                    {{ revenueCard.range }}
                                    <WrIcon name="streamline:move-right" class="size-3.5 text-[#8B90B5] rotate-90" />
                                </span>
                            </div>
                            <div class="mt-2.5 flex h-14 items-end gap-[7px] xl:h-auto xl:flex-1" aria-hidden="true">
                                <span v-for="(bar, i) in revenueCard.bars" :key="i"
                                    class="w-3 rounded-t rounded-b-[2px] bg-[linear-gradient(180deg,#7b7bff,#5b5bef)]"
                                    :class="{ 'bg-[linear-gradient(180deg,#a78bfa,#8b5cf6)]': i === revenueCard.accentIndex }"
                                    :style="{ height: bar + '%' }"></span>
                            </div>
                            <div class="mt-2 flex items-end justify-between">
                                <p class="text-2xl text-[#1E1B4B] dark:text-white">{{ revenueCard.total }}</p>
                                <p class="flex items-center gap-1 text-xl font-medium text-[#34D399]">
                                    <WrIcon name="streamline:arrow-up-1" class="size-4 text-[#34D399]" />
                                    {{ revenueCard.delta }}
                                </p>
                            </div>
                        </div>
                        <div class="flex flex-col justify-between">
                            <h4 class="text-sm leading-none font-medium font-avant-demi text-[#1E1B4B] dark:text-white">
                                {{
                                revenueCard.metricsTitle }}</h4>
                            <div v-for="(metric, i) in revenueCard.metrics" :key="i"
                                class="mt-2 flex items-center gap-2 rounded-[10px] border py-1.5 px-2.5 md:max-lg:gap-1.5 md:max-lg:px-2 border-[rgba(124,108,246,0.3)] bg-[rgba(237,233,254,0.8)] dark:bg-[rgba(24,31,74,0.5)]">
                                <WrIcon :name="metric.icon" class="size-4 text-[#8B90B5] flex-shrink-0" />
                                <span class="flex-1 text-xs text-[#6E6A8F] dark:text-[#8B90B5]">{{ metric.label
                                    }}</span>
                                <span class="text-[13px] text-[#1E1B4B] dark:text-white">{{ metric.value }}</span>
                                <span
                                    :class="['text-xs font-medium', metric.up ? 'text-[#34d399]' : 'text-[#f87171]']">
                                    {{ metric.delta }}
                                </span>
                            </div>
                        </div>
                    </article>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Result cards use a two-layer background (padding-box fill + border-box
   gradient border) — impractical to inline, so it stays here. */
.result-card {
    background:
        linear-gradient(196.53deg,
            #0a0e28 61.83%,
            #0d1232 88.29%) padding-box,
        linear-gradient(129.44deg, #4a52e8 7.54%, #14183f 106.56%) border-box;
}

.my-app-light .result-card {
    background:
        linear-gradient(196.53deg,
            #ffffff 61.83%,
            #f5f2ff 88.29%) padding-box,
        linear-gradient(129.44deg, #8b7bff 7.54%, #cfc8ff 106.56%) border-box;
}

/* Sequential "running" border sweep — a comet of light travels around the
   active prompt + its result card, then moves to the next pair, looping.
   Needs @property + mask-composite, so it stays in the style tag. */
@property --genic-border-angle {
    syntax: "<angle>";
    initial-value: 0deg;
    inherits: false;
}

.prompt-card::after,
.result-card::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1.5px;
    background: conic-gradient(from var(--genic-border-angle),
            transparent 0deg,
            transparent 280deg,
            rgba(196, 181, 253, 0.85) 320deg,
            #c4b5fd 345deg,
            transparent 360deg);
    -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    mask-composite: exclude;
    filter: drop-shadow(0 0 6px rgba(167, 139, 250, 0.7));
    opacity: 0;
    animation: genic-border-sweep 9s linear infinite;
    animation-delay: var(--genic-delay, 0s);
    pointer-events: none;
}

/* Each pair is lit for one-third of the 9s cycle; the per-element delay
   (0s / 3s / 6s) staggers them into a 1 → 2 → 3 → 1… loop. */
@keyframes genic-border-sweep {
    0% {
        --genic-border-angle: 0deg;
        opacity: 0;
    }

    8% {
        opacity: 1;
    }

    28% {
        --genic-border-angle: 360deg;
        opacity: 1;
    }

    33% {
        opacity: 0;
    }

    100% {
        --genic-border-angle: 360deg;
        opacity: 0;
    }
}

.my-app-light .prompt-card::after,
.my-app-light .result-card::after {
    background: conic-gradient(from var(--genic-border-angle),
            transparent 0deg,
            transparent 280deg,
            rgba(124, 92, 252, 0.9) 320deg,
            #7c5cfc 345deg,
            transparent 360deg);
    filter: drop-shadow(0 0 6px rgba(124, 92, 252, 0.5));
}

@media (prefers-reduced-motion: reduce) {

    .prompt-card::after,
    .result-card::after {
        animation: none;
        opacity: 0;
    }
}
</style>
